import { useDroppable } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import WizardAppPreview from '@/assets/WizardAppPreview'
import type { SubtypeState } from '@/lib/action-modules/registry/types'

export const PREVIEW_DROPPABLE_ID = 'subtype-preview-droppable'

// A rendered row is either a real subtype (sortable) or the transient insertion ghost shown
// while dragging a new subtype in from the toolbox.
export type PreviewItem =
  | { kind: 'subtype'; id: number; state: SubtypeState }
  | { kind: 'placeholder'; id: string; state: SubtypeState }

// Aspect ratio of the device-frame SVG (width / height). The list overlay shares the same
// box so its percentage padding lines up with the frame's screen area.
const PREVIEW_ASPECT = '334 / 682'

// Inset of the scrollable screen area inside the phone frame (matches the old CustomerPortal
// preview: 5 % sides, 12 % top for the status bar, 4 % bottom for the home indicator).
const SCREEN_PADDING = '12% 5% 4%'

interface Props {
  items: PreviewItem[]
  selected?: SubtypeState
  onSelect: (subtype: SubtypeState) => void
  onDelete: (subtype: SubtypeState) => void
}

// The composed subtype list, shown inside a mobile device frame roughly as it appears in the
// app. Rows are sortable and the screen area is a drop target so new subtypes can be dropped
// onto an empty list. The dragged row/ghost is rendered by the shared DragOverlay (see
// SubtypesTab), so here the moving row is hidden in place to leave a stable-height gap.
export default function SubtypePreview({ items, selected, onSelect, onDelete }: Props) {
  const { t } = useTranslation('action-modules-wizard')
  const { setNodeRef, isOver } = useDroppable({ id: PREVIEW_DROPPABLE_ID })
  const sortableIds = items.filter(i => i.kind === 'subtype').map(i => i.id)

  return (
    <div className="flex min-h-0 flex-1 justify-center">
      {/* Height-driven phone; width follows the frame aspect ratio and is centered. */}
      <div className="relative h-full max-w-full" style={{ aspectRatio: PREVIEW_ASPECT }}>
        {/* Frame sits behind the list and must not swallow pointer events. */}
        <div className="pointer-events-none absolute inset-0">
          <WizardAppPreview />
        </div>

        {/* Screen area, aligned to the frame via matching padding. */}
        <div className="absolute inset-0 box-border" style={{ padding: SCREEN_PADDING }}>
          <div
            ref={setNodeRef}
            className={cn(
              'flex h-full flex-col gap-2 overflow-y-auto rounded-md [&::-webkit-scrollbar]:hidden',
              isOver && 'bg-primary/5',
            )}
            style={{ scrollbarWidth: 'none' }}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              {items.map(item =>
                item.kind === 'placeholder' ? (
                  <PlaceholderRow key={item.id} state={item.state} />
                ) : (
                  <PreviewRow
                    key={item.id}
                    subtype={item.state}
                    selected={selected === item.state}
                    onSelect={() => onSelect(item.state)}
                    onDelete={() => onDelete(item.state)}
                  />
                ),
              )}
            </SortableContext>
            {items.length === 0 && (
              <div className="m-2 flex flex-1 items-center justify-center rounded-md border-2 border-dashed p-6 text-center text-sm text-muted-foreground">
                {t('tabSubtypes.emptyState')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Invisible spacer marking where a subtype dragged from the toolbox will land. It reserves the
 *  exact height of the incoming row, so the surrounding rows slide apart to open a gap (like
 *  reordering an existing row) — no outline; the floating drag preview shows what will drop. */
function PlaceholderRow({ state }: { state: SubtypeState }) {
  const { t } = useTranslation('action-modules-wizard')
  const name = state.properties.name as string | undefined

  return (
    <div className="rounded-md p-2 opacity-0" aria-hidden>
      {name && <p className="mb-1 text-sm font-medium">{name}</p>}
      {state.definition.editorPreview(state, t)}
    </div>
  )
}

function PreviewRow({
  subtype,
  selected,
  onSelect,
  onDelete,
}: {
  subtype: SubtypeState
  selected: boolean
  onSelect: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation('action-modules-wizard')
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: subtype.editorId })
  const name = subtype.properties.name as string | undefined

  return (
    <div
      ref={setNodeRef}
      // While dragging, hide the row in place (the DragOverlay shows it) so its slot stays a
      // constant-height gap instead of stretching to the drop target.
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 }}
      // Transparent by default so rows blend into the phone screen; a ring marks the selection.
      // ring-inset draws the ring inside the box so the horizontally-clipping scroll container
      // (overflow-y-auto forces overflow-x to auto) can't cut off its left/right edges.
      className={cn('relative rounded-md p-2', selected && 'ring-2 ring-inset ring-primary')}
    >
      {selected && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1 size-6"
          onClick={onDelete}
        >
          <X className="size-4" />
        </Button>
      )}
      {/* Drag handle = the whole row body; clicking (no drag) selects it. */}
      <div {...listeners} {...attributes} onClick={onSelect} className="cursor-grab active:cursor-grabbing">
        {name && <p className="mb-1 text-sm font-medium">{name}</p>}
        {subtype.definition.editorPreview(subtype, t)}
      </div>
    </div>
  )
}
